/**
 * Text-Abbild der Schreibfläche für die Rechtschreibprüfung (T-243).
 *
 * Der Prüfdienst bekommt reinen Text und antwortet mit Positionen darin. Das
 * Abbild merkt sich für jeden Textknoten, wo er im gesendeten Text beginnt —
 * so lassen sich Treffer zurück in DOM-`Range`s übersetzen, ohne den Editor
 * anzufassen.
 *
 * - Zitierter Verlauf (`[data-kmvs-quote]`, `blockquote`) und die Signatur
 *   (`[data-kmvs-signature]`) werden übersprungen: Fremder Text soll keine
 *   Unterstreichungen bekommen, und eine lange Antwortkette machte jede
 *   Prüfung unnötig groß.
 * - Blockgrenzen erzeugen ein `\n` ohne Knoten, damit Sätze nicht über Absätze
 *   hinweg verbunden werden.
 * - Geschütztes Leerzeichen wird als normales Leerzeichen gesendet (gleiche
 *   Länge, die Positionen bleiben gültig).
 */

export interface TextStueck {
    knoten: Text;
    start: number;
}

export interface TextAbbild {
    text: string;
    stuecke: TextStueck[];
}

export const NICHT_PRUEFEN = "[data-kmvs-quote], [data-kmvs-signature], blockquote";

const BLOCK = new Set([
    "ADDRESS", "ARTICLE", "ASIDE", "BLOCKQUOTE", "DD", "DIV", "DL", "DT", "FIGCAPTION", "FIGURE",
    "FOOTER", "H1", "H2", "H3", "H4", "H5", "H6", "HEADER", "HR", "LI", "OL", "P", "PRE", "SECTION",
    "TABLE", "TBODY", "TD", "TFOOT", "TH", "THEAD", "TR", "UL",
]);
const UEBERSPRINGEN = new Set(["SCRIPT", "STYLE", "TEMPLATE", "NOSCRIPT"]);

export function bildeAbbild(root: HTMLElement): TextAbbild {
    let text = "";
    const stuecke: TextStueck[] = [];
    const umbruch = () => {
        if (text.length && !text.endsWith("\n")) text += "\n";
    };
    const lauf = (el: Element) => {
        for (let n = el.firstChild; n; n = n.nextSibling) {
            if (n.nodeType === Node.TEXT_NODE) {
                const t = (n as Text).data;
                if (!t) continue;
                stuecke.push({ knoten: n as Text, start: text.length });
                text += t.replace(/ /g, " ");
            } else if (n.nodeType === Node.ELEMENT_NODE) {
                const e = n as Element;
                if (UEBERSPRINGEN.has(e.tagName)) continue;
                if (e.tagName === "BR") {
                    text += "\n";
                    continue;
                }
                if (e.matches(NICHT_PRUEFEN)) {
                    umbruch();
                    continue;
                }
                const block = BLOCK.has(e.tagName);
                if (block) umbruch();
                lauf(e);
                if (block) umbruch();
            }
        }
    };
    lauf(root);
    return { text, stuecke };
}

/** Index des Stücks, in dem `pos` liegt (Ende inklusive, wenn `amEnde`). */
function stueckAn(abbild: TextAbbild, pos: number, amEnde: boolean): number {
    const s = abbild.stuecke;
    let lo = 0;
    let hi = s.length - 1;
    while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        const a = s[mid].start;
        const b = a + s[mid].knoten.data.length;
        if (amEnde ? pos <= a : pos < a) hi = mid - 1;
        else if (amEnde ? pos > b : pos >= b) lo = mid + 1;
        else return mid;
    }
    return -1;
}

/**
 * Übersetzt einen Treffer in eine DOM-`Range` — auch über mehrere Textknoten
 * (z. B. ein fett gesetzter Wortteil). `null`, wenn die Stelle nicht (mehr) auf
 * Text fällt.
 */
export function bereichFuer(abbild: TextAbbild, offset: number, laenge: number): Range | null {
    if (laenge <= 0) return null;
    const i = stueckAn(abbild, offset, false);
    const j = stueckAn(abbild, offset + laenge, true);
    if (i < 0 || j < 0 || j < i) return null;
    const a = abbild.stuecke[i];
    const b = abbild.stuecke[j];
    const r = a.knoten.ownerDocument.createRange();
    r.setStart(a.knoten, offset - a.start);
    r.setEnd(b.knoten, offset + laenge - b.start);
    return r;
}

/** Umkehrung von `bereichFuer`: Position im Text zu einem DOM-Punkt. */
export function offsetAnPunkt(abbild: TextAbbild, knoten: Node, offsetImKnoten: number): number | null {
    for (const s of abbild.stuecke) {
        if (s.knoten === knoten) return s.start + Math.min(offsetImKnoten, s.knoten.data.length);
    }
    return null;
}
